import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createPopper } from '@popperjs/core';
import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { classNames } from './utils';
const Tooltip = ({ children, text: content, placement = 'bottom', tooltipClassName, wrapperClassName, hideArrow, disabled, color = 'black', }) => {
    const [visible, setVisible] = useState(false);
    // Track hover states for the trigger and the tooltip
    const [isHoveringTrigger, setIsHoveringTrigger] = useState(false);
    const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);
    // References for the trigger (referenceRef), tooltip (popperRef), and arrow
    const referenceRef = useRef(null);
    const popperRef = useRef(null);
    const arrowRef = useRef(null);
    // Keep track of the Popper instance
    const popperInstanceRef = useRef(null);
    // Initialize Popper once references are available
    useEffect(() => {
        if (referenceRef.current && popperRef.current) {
            popperInstanceRef.current = createPopper(referenceRef.current, popperRef.current, {
                placement,
                strategy: 'absolute', // or 'fixed' if you prefer
                modifiers: [
                    {
                        name: 'offset',
                        options: {
                            offset: [0, 8],
                        },
                    },
                    ...(hideArrow
                        ? []
                        : [
                            {
                                name: 'arrow',
                                options: {
                                    element: arrowRef.current,
                                    padding: 5,
                                },
                            },
                        ]),
                    {
                        // This ensures the boundary is the viewport (entire screen)
                        // so the tooltip won't be clipped by any parent container.
                        name: 'preventOverflow',
                        options: {
                            boundary: 'viewport',
                            padding: 16, // Add 16px padding from screen edges
                            // or boundary: 'window'
                        },
                    },
                ],
            });
        }
        return () => {
            var _a;
            // Cleanup on unmount
            (_a = popperInstanceRef.current) === null || _a === void 0 ? void 0 : _a.destroy();
            popperInstanceRef.current = null;
        };
    }, [hideArrow, placement]);
    // Whenever hover states change, decide if the tooltip should be visible
    useEffect(() => {
        // If hovering trigger OR hovering tooltip, show it
        setVisible(!disabled && (isHoveringTrigger || isHoveringTooltip));
    }, [isHoveringTrigger, isHoveringTooltip, disabled]);
    // Update popper when tooltip becomes visible or content changes
    useEffect(() => {
        var _a;
        if (visible) {
            (_a = popperInstanceRef.current) === null || _a === void 0 ? void 0 : _a.update();
        }
    }, [visible, content]);
    // The tooltip element that we'll portal to <body>
    const tooltipElement = (_jsxs("div", { ref: popperRef, role: "tooltip", 
        // Conditional Tailwind classes
        className: classNames('tooltip', color === 'white' && 'tooltip-white', visible
            ? 'scale-100 opacity-100'
            : 'pointer-events-none scale-95 opacity-0', 'absolute z-50 box-border max-w-[90vw] origin-center transform rounded-md p-2 text-sm shadow-md transition-all duration-150 ease-in-out', color === 'white'
            ? 'border border-gray-200 bg-white text-gray-800'
            : 'bg-gray-800 text-white', tooltipClassName), onMouseEnter: () => setIsHoveringTooltip(true), onMouseLeave: () => setIsHoveringTooltip(false), children: [content, !hideArrow && _jsx("div", { ref: arrowRef, "data-popper-arrow": true, className: "arrow" })] }));
    return (_jsxs(_Fragment, { children: [_jsx("div", { ref: referenceRef, onMouseEnter: () => setIsHoveringTrigger(true), onMouseLeave: () => setIsHoveringTrigger(false), className: classNames('cursor-pointer', wrapperClassName), children: children }), ReactDOM.createPortal(tooltipElement, document.body)] }));
};
export default Tooltip;
